import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function EventsManager({ events, onChange }) {
  const [creating, setCreating] = useState(false);
  const [date, setDate] = useState("");
  const [track, setTrack] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !track) { toast.error("Date and track are required."); return; }
    setBusy(true);
    try {
      await api.post("/admin/events", { date, track, name, description });
      toast.success("Event created");
      setDate(""); setTrack(""); setName(""); setDescription(""); setCreating(false);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const removeEvent = async (id) => {
    if (!window.confirm("Delete this event and all its classes? Photos will be hidden from the gallery.")) return;
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success("Event deleted");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-8" data-testid="events-manager">
      <div className="flex justify-between items-end">
        <p className="text-sm text-white/60">Add a race day, then add classes (e.g. 65cc, 85cc Big Wheel, Vets).</p>
        {!creating && (
          <button
            onClick={() => setCreating(true)}
            className="bg-white text-black px-6 py-3 text-xs uppercase tracking-[0.3em] hover:bg-white/90"
            data-testid="new-event-button"
          >
            + New event
          </button>
        )}
      </div>

      {creating && (
        <form
          onSubmit={submit}
          className="border border-white/10 p-6 grid grid-cols-1 md:grid-cols-2 gap-6 bg-[#0a0a0a]"
          data-testid="new-event-form"
        >
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
              className="input-minimal"
              data-testid="event-date"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Track</label>
            <input
              value={track}
              onChange={(e) => setTrack(e.target.value)}
              required
              placeholder="e.g. Foxhill MX"
              className="input-minimal"
              data-testid="event-track-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Event name (optional)</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Round 4 · British Championship"
              className="input-minimal"
              data-testid="event-name-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Description (optional)</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="input-minimal"
              data-testid="event-description-input"
            />
          </div>
          <div className="md:col-span-2 flex gap-3">
            <button
              type="submit"
              disabled={busy}
              className="bg-white text-black px-8 py-3 text-xs uppercase tracking-[0.3em] hover:bg-white/90 disabled:opacity-50"
              data-testid="submit-event"
            >
              {busy ? "Saving…" : "Create event"}
            </button>
            <button
              type="button"
              onClick={() => setCreating(false)}
              className="border border-white/20 px-8 py-3 text-xs uppercase tracking-[0.3em] hover:border-white/60"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {events.length === 0 ? (
        <p className="text-white/50 text-sm py-8">No events yet. Create your first one above.</p>
      ) : (
        <div className="space-y-4">
          {events.map((ev) => (
            <EventRow key={ev.id} event={ev} onChange={onChange} onDelete={() => removeEvent(ev.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function EventRow({ event, onChange, onDelete }) {
  const [open, setOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [busy, setBusy] = useState(false);

  const addGroup = async (e) => {
    e.preventDefault();
    if (!groupName.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/events/${event.id}/groups`, { name: groupName.trim() });
      setGroupName("");
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const removeGroup = async (gid) => {
    if (!window.confirm("Delete this class? Photos will be hidden.")) return;
    try {
      await api.delete(`/admin/groups/${gid}`);
      onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="border border-white/10 bg-[#0a0a0a]" data-testid={`event-row-${event.id}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-center px-5 py-4 hover:bg-white/5 text-left"
      >
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-white/40">{event.date}</p>
          <h3 className="font-serif text-2xl mt-1">{event.track}</h3>
          {event.name && <p className="text-sm text-white/60 mt-1">{event.name}</p>}
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-[0.2em] text-white/50">
            {event.group_count} {event.group_count === 1 ? "class" : "classes"} · {event.photo_count} photos
          </p>
          <p className="text-xs text-white/30 mt-1">{open ? "Collapse" : "Expand"}</p>
        </div>
      </button>

      {open && (
        <div className="border-t border-white/10 px-5 py-5 space-y-4">
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40">Classes</p>
            {(event.groups || []).length === 0 ? (
              <p className="text-white/40 text-sm">No classes yet — add one below.</p>
            ) : (
              <ul className="space-y-1">
                {event.groups.map((g) => (
                  <li key={g.id} className="flex justify-between items-center py-2 border-b border-white/5" data-testid={`group-row-${g.id}`}>
                    <div>
                      <span className="font-serif text-lg">{g.name}</span>
                      <span className="text-xs text-white/40 ml-3">{g.photo_count} photos</span>
                    </div>
                    <button
                      onClick={() => removeGroup(g.id)}
                      className="text-xs uppercase tracking-[0.2em] text-red-400 hover:text-red-300"
                      data-testid={`delete-group-${g.id}`}
                    >
                      Delete
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <form onSubmit={addGroup} className="flex gap-3 items-end pt-2" data-testid={`add-group-form-${event.id}`}>
            <div className="flex-1">
              <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1">Add class</label>
              <input
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="e.g. 85cc Big Wheel"
                className="input-minimal"
                data-testid={`new-group-input-${event.id}`}
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="bg-white text-black px-6 py-3 text-xs uppercase tracking-[0.3em] hover:bg-white/90 disabled:opacity-50"
              data-testid={`add-group-button-${event.id}`}
            >
              Add
            </button>
          </form>

          <div className="pt-4 border-t border-white/5">
            <button
              onClick={onDelete}
              className="text-xs uppercase tracking-[0.2em] text-red-400 hover:text-red-300"
              data-testid={`delete-event-${event.id}`}
            >
              Delete entire event
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
